import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  TextField,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  NotificationsOff,
  Settings,
  CheckCircle,
  Schedule,
  Close,
  Snooze,
  MonetizationOn,
  Warning,
  Info
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { ExpensesService } from '../../utils/expenses-service';
import {
  ExpenseNotification,
  ExpenseNotificationsResponse,
  ExpenseNotificationPreferences,
  CreateExpenseRequest
} from '../../types';
import ExpenseReminderModal from './ExpenseReminderModal';

interface ExpenseNotificationsProps {
  onNotificationClick?: (notification: ExpenseNotification) => void;
  onUnreadCountChange?: (count: number) => void;
  className?: string;
  maxItems?: number;
}

const ExpenseNotifications: React.FC<ExpenseNotificationsProps> = ({
  onNotificationClick,
  onUnreadCountChange,
  className = '',
  maxItems = 10
}) => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<ExpenseNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  
  // Preferences dialog
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState<ExpenseNotificationPreferences | null>(null);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  
  // Reminder modal
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<ExpenseNotification | null>(null);

  const open = Boolean(anchorEl);

  const loadNotifications = useCallback(async () => {
    try {
      setError(null);
      const response = await ExpensesService.getExpenseNotifications({ limit: maxItems });
      setNotifications(response.notifications);
      setUnreadCount(response.unreadCount);
      setOverdueCount(response.overdueCount);
    } catch (err) {
      console.error('Error loading expense notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [maxItems]);

  useEffect(() => {
    if ((user as any)?.userGroup === 'hosts') {
      loadNotifications();
      loadPreferences();
      
      // Set up polling for new notifications
      const interval = setInterval(loadNotifications, 60000); // Poll every minute
      return () => clearInterval(interval);
    }
  }, [user, loadNotifications]);

  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount + overdueCount);
    }
  }, [unreadCount, overdueCount, onUnreadCountChange]);

  const loadPreferences = async () => {
    try {
      const prefs = await ExpensesService.getExpenseNotificationPreferences();
      setPreferences(prefs);
    } catch (err) {
      console.error('Error loading notification preferences:', err);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: ExpenseNotification) => {
    // Mark as read if not already read
    if (notification.status !== 'read') {
      await markAsRead(notification.notificationId);
    }

    // Handle different notification types
    if (notification.type === 'expense_reminder' && notification.expenseData?.reminderId) {
      setSelectedReminder(notification);
      setReminderModalOpen(true);
    }

    // Call callback if provided
    if (onNotificationClick) {
      onNotificationClick(notification);
    }

    handleClose();
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await ExpensesService.markExpenseNotificationAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.notificationId === notificationId 
            ? { ...notif, status: 'read', readAt: new Date().toISOString() }
            : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setSnackbarMessage('Failed to mark notification as read');
    }
  };

  const dismissNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    try {
      await ExpensesService.dismissExpenseNotification(notificationId);
      
      // Remove from local state
      setNotifications(prev => prev.filter(notif => notif.notificationId !== notificationId));
      setSnackbarMessage('Notification dismissed');
    } catch (err) {
      console.error('Error dismissing notification:', err);
      setSnackbarMessage('Failed to dismiss notification');
    }
  };

  const snoozeNotification = async (notificationId: string, hours: number, event: React.MouseEvent) => {
    event.stopPropagation();
    
    try {
      await ExpensesService.snoozeExpenseNotification(notificationId, hours);
      
      // Remove from local state temporarily
      setNotifications(prev => prev.filter(notif => notif.notificationId !== notificationId));
      setSnackbarMessage(`Notification snoozed for ${hours} hours`);
    } catch (err) {
      console.error('Error snoozing notification:', err);
      setSnackbarMessage('Failed to snooze notification');
    }
  };

  const updatePreferences = async (newPreferences: Partial<ExpenseNotificationPreferences>) => {
    setPreferencesLoading(true);
    try {
      const updated = await ExpensesService.updateExpenseNotificationPreferences(newPreferences);
      setPreferences(updated);
      setSnackbarMessage('Notification preferences updated');
    } catch (err) {
      console.error('Error updating preferences:', err);
      setSnackbarMessage('Failed to update preferences');
    } finally {
      setPreferencesLoading(false);
    }
  };

  const getNotificationIcon = (type: ExpenseNotification['type']) => {
    switch (type) {
      case 'expense_reminder':
        return <Schedule color="primary" />;
      case 'expense_overdue':
        return <Warning color="error" />;
      case 'recurring_expense_created':
        return <MonetizationOn color="success" />;
      case 'expense_report_ready':
        return <Info color="info" />;
      default:
        return <Notifications />;
    }
  };

  const getPriorityColor = (priority: ExpenseNotification['priority']) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInMinutes < 1440) { // 24 hours
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if ((user as any)?.userGroup !== 'hosts') {
    return null;
  }

  const totalBadgeCount = unreadCount + overdueCount;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <IconButton
        onClick={handleClick}
        className={className}
        color="inherit"
        aria-label="expense notifications"
      >
        <Badge badgeContent={totalBadgeCount} color="error">
          {overdueCount > 0 ? (
            <NotificationsActive color="error" />
          ) : unreadCount > 0 ? (
            <NotificationsActive color="primary" />
          ) : (
            <Notifications />
          )}
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 400, maxHeight: 600 }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Expense Notifications
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {totalBadgeCount > 0 && (
                <Chip 
                  label={`${totalBadgeCount} new`} 
                  size="small" 
                  color={overdueCount > 0 ? 'error' : 'primary'} 
                />
              )}
              <IconButton 
                size="small" 
                onClick={() => setPreferencesOpen(true)}
                aria-label="notification settings"
              >
                <Settings />
              </IconButton>
            </Box>
          </Box>
          
          {overdueCount > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              You have {overdueCount} overdue expense reminder{overdueCount > 1 ? 's' : ''}
            </Alert>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Notifications List */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Loading notifications...
              </Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <NotificationsOff sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No expense notifications
              </Typography>
            </Box>
          ) : (
            notifications.map((notification, index) => (
              <React.Fragment key={notification.notificationId}>
                <MenuItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    alignItems: 'flex-start',
                    py: 2,
                    backgroundColor: notification.status !== 'read' ? 'action.hover' : 'transparent'
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                    <Box sx={{ mt: 0.5 }}>
                      {getNotificationIcon(notification.type)}
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: notification.status !== 'read' ? 600 : 400,
                            color: notification.priority === 'high' ? 'error.main' : 'text.primary'
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(notification.createdAt)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {notification.message}
                      </Typography>
                      
                      {notification.expenseData && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip 
                            label={notification.expenseData.categoryName} 
                            size="small" 
                            variant="outlined" 
                          />
                          {notification.expenseData.listingTitle && (
                            <Chip 
                              label={notification.expenseData.listingTitle} 
                              size="small" 
                              variant="outlined" 
                            />
                          )}
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={notification.priority} 
                          size="small" 
                          color={getPriorityColor(notification.priority)} 
                          variant="outlined" 
                        />
                        
                        {notification.status !== 'read' && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                        )}
                        
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                          {notification.type === 'expense_reminder' && (
                            <IconButton 
                              size="small" 
                              onClick={(e) => snoozeNotification(notification.notificationId, 24, e)}
                              title="Snooze for 24 hours"
                            >
                              <Snooze fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton 
                            size="small" 
                            onClick={(e) => dismissNotification(notification.notificationId, e)}
                            title="Dismiss"
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 0 && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Button
              onClick={loadNotifications}
              size="small"
              disabled={loading}
            >
              Refresh notifications
            </Button>
          </Box>
        )}
      </Menu>

      {/* Notification Preferences Dialog */}
      <Dialog 
        open={preferencesOpen} 
        onClose={() => setPreferencesOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Expense Notification Preferences</DialogTitle>
        <DialogContent>
          {preferences && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Email Notifications
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.emailNotifications.expenseReminders}
                    onChange={(e) => updatePreferences({
                      emailNotifications: {
                        ...preferences.emailNotifications,
                        expenseReminders: e.target.checked
                      }
                    })}
                  />
                }
                label="Expense reminders"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.emailNotifications.overdueExpenses}
                    onChange={(e) => updatePreferences({
                      emailNotifications: {
                        ...preferences.emailNotifications,
                        overdueExpenses: e.target.checked
                      }
                    })}
                  />
                }
                label="Overdue expense alerts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.emailNotifications.weeklyReports}
                    onChange={(e) => updatePreferences({
                      emailNotifications: {
                        ...preferences.emailNotifications,
                        weeklyReports: e.target.checked
                      }
                    })}
                  />
                }
                label="Weekly expense reports"
              />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                In-App Notifications
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.inAppNotifications.expenseReminders}
                    onChange={(e) => updatePreferences({
                      inAppNotifications: {
                        ...preferences.inAppNotifications,
                        expenseReminders: e.target.checked
                      }
                    })}
                  />
                }
                label="Expense reminders"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.inAppNotifications.overdueExpenses}
                    onChange={(e) => updatePreferences({
                      inAppNotifications: {
                        ...preferences.inAppNotifications,
                        overdueExpenses: e.target.checked
                      }
                    })}
                  />
                }
                label="Overdue expense alerts"
              />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Reminder Timing
              </Typography>
              <TextField
                label="Default reminder days before due"
                type="number"
                value={preferences.reminderTiming.defaultReminderDays}
                onChange={(e) => updatePreferences({
                  reminderTiming: {
                    ...preferences.reminderTiming,
                    defaultReminderDays: parseInt(e.target.value) || 1
                  }
                })}
                inputProps={{ min: 1, max: 30 }}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Follow-up reminder days after overdue"
                type="number"
                value={preferences.reminderTiming.followUpReminderDays}
                onChange={(e) => updatePreferences({
                  reminderTiming: {
                    ...preferences.reminderTiming,
                    followUpReminderDays: parseInt(e.target.value) || 7
                  }
                })}
                inputProps={{ min: 1, max: 30 }}
                fullWidth
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreferencesOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expense Reminder Modal */}
      {selectedReminder && (
        <ExpenseReminderModal
          open={reminderModalOpen}
          onClose={() => {
            setReminderModalOpen(false);
            setSelectedReminder(null);
          }}
          reminder={{
            reminderId: selectedReminder.expenseData?.reminderId || '',
            recurringExpenseId: selectedReminder.expenseData?.recurringExpenseId || '',
            categoryName: selectedReminder.expenseData?.categoryName || '',
            dueDate: selectedReminder.expenseData?.dueDate || '',
            listingTitle: selectedReminder.expenseData?.listingTitle
          }}
          onExpenseCreated={(expense) => {
            // Remove the notification from local state
            setNotifications(prev => 
              prev.filter(notif => notif.notificationId !== selectedReminder?.notificationId)
            );
            
            setSnackbarMessage('Expense reminder completed successfully');
            setReminderModalOpen(false);
            setSelectedReminder(null);
          }}
        />
      )}

      {/* Snackbar for messages */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
    </>
  );
};

export default ExpenseNotifications;